import React, { useState, useMemo } from "react";
import { submitLeaveShared } from "../../../settings/StaffSelfInfoPanel";
import useBnavFab from "../../../shared/useBnavFab";
import Modal from "../../../shared/Modal";

/* ══ MY LEAVE PANEL ═════════════════════════════════════════
   Staff-side leave surface. Visual language matches the staff Expenses,
   Suppliers, and My Finances panels:
     – `.exp-hero` title + subtitle + `.exp-summary-glass` KPI card
     – `.exp-status-tabs` segmented control to filter by status
     – `.exp-merged-card` list rows with status pills + day-range icon
     – `.stf-detail-overlay` bottom-sheet for request detail + create

   Intentionally bespoke: the main-workspace StaffModule is an owner / HR
   view (roster, payroll, docs, loans, termination). Staff-side leave is
   just "submit a form + track my requests" — no subset of StaffModule
   matches, so forcing reuse would gut too much. Still uses the shared
   submitLeaveShared() from StaffSelfInfoPanel.

   The bottom-nav "+" FAB (see StaffView manifest: hasFab:true) dispatches
   `bnav:fab`; useBnavFab catches it here and opens the leave-request
   modal — no inline add button, matching the FAB-is-the-add-affordance
   convention used by the other staff panels. */
export default function LeavePanel({state,dispatch,user,myStaffRecord,platformCtx,isRTL}){
  const [showLeave,setShowLeave]=useState(false);
  const [leaveForm,setLeaveForm]=useState({startDate:"",endDate:"",notes:""});
  const [statusFilter,setStatusFilter]=useState("All");
  const [detailReq,setDetailReq]=useState(null);

  /* FAB — mounted before the early return so the hook order is stable
     even if the staff record isn't linked yet. */
  useBnavFab(()=>setShowLeave(true));

  const myLeave=useMemo(
    ()=>(state.leaveRequests||[]).filter(r=>r.staffId===myStaffRecord?.id),
    [state.leaveRequests,myStaffRecord]
  );

  const dayCount=(start,end)=>{
    if(!start||!end)return 0;
    const s=new Date(start),e=new Date(end);
    if(isNaN(s)||isNaN(e))return 0;
    return Math.max(1,Math.round((e-s)/86400000)+1);
  };

  const statusCounts=useMemo(()=>({
    All:myLeave.length,
    Pending:myLeave.filter(r=>r.status==="pending").length,
    Approved:myLeave.filter(r=>r.status==="approved").length,
    Rejected:myLeave.filter(r=>r.status==="rejected").length,
  }),[myLeave]);

  const approvedDays=useMemo(
    ()=>myLeave.filter(r=>r.status==="approved").reduce((n,r)=>n+dayCount(r.startDate,r.endDate),0),
    [myLeave]
  );

  const filtered=statusFilter==="All"
    ?myLeave
    :myLeave.filter(r=>r.status===statusFilter.toLowerCase());

  const handleSubmitLeave=()=>{
    if(submitLeaveShared({leaveForm,staffRecord:myStaffRecord,state,dispatch,platformCtx,isRTL})){
      setShowLeave(false);setLeaveForm({startDate:"",endDate:"",notes:""});
    }
  };

  const statusBadge=(s)=>s==="approved"?"status-a":s==="rejected"?"status-rej":"status-p";
  const statusLabel=(s)=>isRTL?(s==="approved"?"موافق":s==="rejected"?"مرفوض":"معلق"):s.charAt(0).toUpperCase()+s.slice(1);
  const statusColor=(s)=>s==="approved"?"var(--grn)":s==="rejected"?"var(--red)":"var(--gold)";
  const statusBg   =(s)=>s==="approved"?"var(--grn-bg)":s==="rejected"?"var(--red-bg)":"var(--gold-bg)";

  const STATUSES=["All","Pending","Approved","Rejected"];
  const statusTabLabel=(s)=>{
    if(!isRTL)return s;
    return s==="All"?"الكل":s==="Pending"?"معلق":s==="Approved"?"موافق":"مرفوض";
  };

  return(
    <div className="page inc-page-bg">
      {/* ═══ MOBILE: Hero — matches Tasks/Suppliers vocabulary ═══ */}
      <div className="stf-hero">
        <div className="stf-hero-greeting">{isRTL?"الإجازات":"My Leaves"}</div>
        <div className="stf-hero-sub">{isRTL?"طلبات الإجازة · الموافقات · السجل":"Leave requests · Approvals · History"}</div>
        <div className="stf-summary-glass">
          <div className="stf-summary-row">
            <div className="stf-summary-item"><div className="stf-summary-val" style={{color:"var(--ink)"}}>{approvedDays}</div><div className="stf-summary-lbl">{isRTL?"أيام":"Days"}</div></div>
            <div className="stf-summary-item"><div className="stf-summary-val" style={{color:"var(--gold)"}}>{statusCounts.Pending}</div><div className="stf-summary-lbl">{isRTL?"معلق":"Pending"}</div></div>
            <div className="stf-summary-item"><div className="stf-summary-val" style={{color:"var(--grn)"}}>{statusCounts.Approved}</div><div className="stf-summary-lbl">{isRTL?"موافق":"Approved"}</div></div>
            <div className="stf-summary-item"><div className="stf-summary-val" style={{color:statusCounts.Rejected?"var(--red)":"var(--g400)"}}>{statusCounts.Rejected}</div><div className="stf-summary-lbl">{isRTL?"مرفوض":"Rejected"}</div></div>
          </div>
        </div>
      </div>

      {/* Status tabs — filter leave requests */}
      <div className="exp-status-tabs">
        {STATUSES.map(s=>(
          <button key={s} className={`exp-status-tab ${statusFilter===s?"active":""}`} onClick={()=>setStatusFilter(s)}>
            {statusTabLabel(s)}{statusCounts[s]>0?` · ${statusCounts[s]}`:""}
          </button>
        ))}
      </div>

      {/* Unlinked notice — surfaced here so staff without a record can see
          why the FAB's submit button will be disabled */}
      {!myStaffRecord&&(
        <div style={{padding:"0 16px",marginBottom:14}}>
          <div className="exp-merged-card" style={{padding:"14px 18px",fontSize:13,color:"var(--red)",borderColor:"var(--red-bd)"}}>
            {isRTL?"حسابك غير مرتبط بسجل موظف. تواصل مع مديرك لتفعيل طلبات الإجازة.":"Your account is not linked to a staff record. Ask your manager to activate leave requests."}
          </div>
        </div>
      )}

      {/* ═══ LEAVE REQUESTS ═══ */}
      <div style={{padding:"0 16px",marginBottom:18}}>
        <div style={{fontSize:11,fontWeight:700,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em",margin:"4px 2px 10px"}}>
          {isRTL?"طلباتي":"My Requests"}
        </div>
        {filtered.length>0?(
          <div className="exp-merged-card">
            {filtered.map((r,i)=>{
              const d=dayCount(r.startDate,r.endDate);
              return(
                <div key={r.id} className="exp-row" style={{borderBottom:i<filtered.length-1?"1px solid var(--g100)":"none"}} onClick={()=>setDetailReq(r)}>
                  <div className="exp-row-icon" style={{background:statusBg(r.status)}}>
                    <span style={{color:statusColor(r.status),fontSize:13,fontWeight:700}}>{d}d</span>
                  </div>
                  <div className="exp-row-info">
                    <div className="exp-row-name">{r.startDate} → {r.endDate}</div>
                    <div className="exp-row-sub">{r.notes?r.notes:(isRTL?"بدون ملاحظات":"No notes")} · {isRTL?"طُلب":"requested"} {r.requestedAt||"—"}</div>
                  </div>
                  <span className={`exp-m-badge ${statusBadge(r.status)}`} style={{flexShrink:0}}>{statusLabel(r.status)}</span>
                </div>
              );
            })}
          </div>
        ):(
          <div className="exp-merged-card" style={{textAlign:"center",padding:"28px 20px",color:"var(--g400)",fontSize:13}}>
            {statusFilter==="All"
              ?(isRTL?"لا طلبات إجازة بعد — اضغط + لطلب إجازة":"No leave requests yet — tap + to request one")
              :(isRTL?"لا نتائج بهذا الفلتر":"No requests match this filter")}
          </div>
        )}
      </div>

      {/* ═══ LEAVE DETAIL SHEET ═══ */}
      {detailReq&&(
        <Modal><div className="stf-detail-overlay" onClick={()=>setDetailReq(null)}>
          <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
            <div className="stf-detail-handle"/>
            <div className="stf-detail-header">
              <div className="stf-detail-title">{detailReq.startDate} → {detailReq.endDate}</div>
              <button className="stf-detail-close" onClick={()=>setDetailReq(null)}>✕</button>
            </div>
            <div className="stf-detail-body">
              <div className="exp-detail-amount" style={{color:statusColor(detailReq.status)}}>
                {dayCount(detailReq.startDate,detailReq.endDate)} {isRTL?"أيام":"days"}
              </div>
              <div className="exp-detail-badges">
                <span className="exp-detail-badge" style={{background:statusBg(detailReq.status),color:statusColor(detailReq.status)}}>{statusLabel(detailReq.status)}</span>
              </div>
              <div className="exp-detail-section">
                <div className="exp-detail-section-title">{isRTL?"التفاصيل":"Details"}</div>
                <div className="exp-detail-row"><span className="exp-detail-row-label">{isRTL?"من":"From"}</span><span className="exp-detail-row-value mono">{detailReq.startDate}</span></div>
                <div className="exp-detail-row"><span className="exp-detail-row-label">{isRTL?"إلى":"To"}</span><span className="exp-detail-row-value mono">{detailReq.endDate}</span></div>
                <div className="exp-detail-row"><span className="exp-detail-row-label">{isRTL?"المدة":"Duration"}</span><span className="exp-detail-row-value">{dayCount(detailReq.startDate,detailReq.endDate)} {isRTL?"أيام":"days"}</span></div>
                {detailReq.requestedAt&&<div className="exp-detail-row"><span className="exp-detail-row-label">{isRTL?"تاريخ الطلب":"Requested"}</span><span className="exp-detail-row-value mono">{detailReq.requestedAt}</span></div>}
              </div>
              {detailReq.notes&&(
                <div className="exp-detail-section">
                  <div className="exp-detail-section-title">{isRTL?"ملاحظات":"Notes"}</div>
                  <div className="exp-detail-notes">{detailReq.notes}</div>
                </div>
              )}
              {detailReq.status==="approved"&&detailReq.approvedBy&&(
                <div className="exp-detail-section">
                  <div className="exp-detail-section-title">{isRTL?"الموافقة":"Approval"}</div>
                  <div className="exp-detail-row"><span className="exp-detail-row-label">{isRTL?"موافق بواسطة":"Approved by"}</span><span className="exp-detail-row-value" style={{color:"var(--grn)"}}>{detailReq.approvedBy}</span></div>
                </div>
              )}
              {detailReq.status==="rejected"&&(
                <div className="exp-detail-section">
                  <div className="exp-detail-section-title">{isRTL?"الرفض":"Rejection"}</div>
                  {detailReq.rejectedBy&&<div className="exp-detail-row"><span className="exp-detail-row-label">{isRTL?"مرفوض بواسطة":"Rejected by"}</span><span className="exp-detail-row-value" style={{color:"var(--red)"}}>{detailReq.rejectedBy}</span></div>}
                  {detailReq.rejectReason&&<div className="exp-detail-notes" style={{marginTop:10}}>{detailReq.rejectReason}</div>}
                </div>
              )}
            </div>
          </div>
        </div></Modal>
      )}

      {/* ═══ NEW LEAVE REQUEST SHEET (opened by "+" FAB) ═══ */}
      {showLeave&&(
        <Modal><div className="stf-detail-overlay" onClick={()=>setShowLeave(false)}>
          <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
            <div className="stf-detail-handle"/>
            <div className="stf-detail-header">
              <div className="stf-detail-title">{isRTL?"طلب إجازة جديد":"New Leave Request"}</div>
              <button className="stf-detail-close" onClick={()=>setShowLeave(false)}>✕</button>
            </div>
            <div className="stf-detail-body">
              <div className="fr">
                <div className="fg"><label className="fl">{isRTL?"من":"From"}</label><input className="fi" type="date" value={leaveForm.startDate} onChange={e=>setLeaveForm(p=>({...p,startDate:e.target.value}))}/></div>
                <div className="fg"><label className="fl">{isRTL?"إلى":"To"}</label><input className="fi" type="date" value={leaveForm.endDate} onChange={e=>setLeaveForm(p=>({...p,endDate:e.target.value}))}/></div>
              </div>
              {leaveForm.startDate&&leaveForm.endDate&&(
                <div style={{fontSize:12,color:"var(--g500)",margin:"6px 0 10px"}}>
                  → {isRTL?"المدة:":"Duration:"} {dayCount(leaveForm.startDate,leaveForm.endDate)} {isRTL?"أيام":"days"}
                </div>
              )}
              <div className="fg" style={{marginBottom:12}}>
                <label className="fl">{isRTL?"ملاحظات":"Notes"} <span style={{color:"var(--g400)",fontWeight:400}}>{isRTL?"(اختياري)":"(optional)"}</span></label>
                <textarea className="fta" value={leaveForm.notes} onChange={e=>setLeaveForm(p=>({...p,notes:e.target.value}))} style={{minHeight:64}} placeholder={isRTL?"السبب أو أي تفاصيل إضافية…":"Reason or any additional details…"}/>
              </div>
              {!myStaffRecord&&(
                <div style={{fontSize:12,color:"var(--red)",marginTop:4}}>
                  {isRTL?"لم يتم ربط حسابك بسجل موظف":"Your account is not linked to a staff record. Ask your manager."}
                </div>
              )}
            </div>
            <div style={{padding:"12px 20px 24px",display:"flex",gap:8}}>
              <button className="btn btn-g" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowLeave(false)}>{isRTL?"إلغاء":"Cancel"}</button>
              <button className="btn btn-p" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={handleSubmitLeave} disabled={!myStaffRecord}>{isRTL?"إرسال":"Submit"}</button>
            </div>
          </div>
        </div></Modal>
      )}
    </div>
  );
}
